import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { API, useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import {
  Folder, Plus, ClipboardList, Trash2,
  X, Archive, AlertTriangle, CalendarDays, Clock, Pencil, Check,
  UsersRound, Baby,
} from "lucide-react";
import AppTopBar from "@/components/AppTopBar";
import GroupPortraitsDialog from "@/components/GroupPortraitsDialog";  // legacy — retained for other flows if present

// Turns an ISO date into "MMM D" (e.g. "Feb 16"). Short so both dates
// fit in the compact job-card footer without wrapping.
function _shortDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// Due-date meta. Prefers the admin-editable ``due_date`` field on the
// job; falls back to auto-computed ``created_at + 3 days`` for legacy
// jobs. Returns an object with the formatted label, an ``overdue``
// flag (only for open jobs), and the ISO string used by the inline
// edit picker.
function _dueMeta(job) {
  if (!job.created_at && !job.due_date) {
    return { date: "—", overdue: false, dueIso: null, source: "none" };
  }
  const explicit = job.due_date ? new Date(job.due_date) : null;
  const created = job.created_at ? new Date(job.created_at) : null;
  const due = explicit
    || (created ? new Date(created.getTime() + 3 * 24 * 60 * 60 * 1000) : null);
  if (!due) return { date: "—", overdue: false, dueIso: null, source: "none" };
  const overdue = job.status !== "closed" && due.getTime() < Date.now();
  return {
    date: _shortDate(due.toISOString()),
    overdue,
    dueIso: due.toISOString(),
    source: explicit ? "manual" : "auto",
  };
}

function StatusPill({ status }) {
  const cls =
    status === "ready_for_review"
      ? "bg-orange-50 text-orange-700 border-orange-200"
      : status === "closed"
      ? "bg-slate-100 text-slate-700 border-slate-300"
      : "bg-blue-50 text-blue-700 border-blue-200";
  const label =
    status === "ready_for_review" ? "Ready for review"
    : status === "closed" ? "Closed" : "In progress";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold tracking-wide border ${cls}`}
    >
      {label}
    </span>
  );
}

export default function JobsPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", assigned_user_id: "" });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirming, setConfirming] = useState(false);
  const [typedConfirm, setTypedConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [filter, setFilter] = useState("all");  // all | in_progress | ready_for_review | closed
  // Phase 99: Group → Portraits dialog toggle.
  const [portraitsOpen, setPortraitsOpen] = useState(false);
  // Phase 82: admin inline-edit for a job's due date. ``editingDueId``
  // is the job id currently being edited; ``editingDueValue`` holds
  // the yyyy-mm-dd string bound to the ``<input type="date">``. Only
  // one row is editable at a time.
  const [editingDueId, setEditingDueId] = useState(null);
  const [editingDueValue, setEditingDueValue] = useState("");
  const [savingDue, setSavingDue] = useState(false);

  const isAdmin = user?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/jobs`);
      setJobs(data);
      if (isAdmin) {
        const { data: us } = await axios.get(`${API}/users`);
        setUsers(us);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [user]);

  const createJob = async () => {
    try {
      await axios.post(`${API}/jobs`, form);
      toast.success("Job created");
      setCreating(false);
      setForm({ name: "", description: "", assigned_user_id: "" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  // Phase 82: persist an admin-picked due date onto the job. Empty
  // string clears the explicit date (backend falls back to
  // ``created_at + 3d``). The card row exits edit mode on success.
  const saveDueDate = async (jobId) => {
    setSavingDue(true);
    try {
      const iso = editingDueValue ? new Date(editingDueValue).toISOString() : null;
      const { data } = await axios.patch(`${API}/jobs/${jobId}`, { due_date: iso });
      setJobs((prev) => prev.map((j) => (j.id === jobId ? data : j)));
      setEditingDueId(null);
      setEditingDueValue("");
      toast.success(iso ? "Due date updated" : "Due date reset to default");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSavingDue(false);
    }
  };

  const startEditingDue = (job, dueIso) => {
    setEditingDueId(job.id);
    // Pre-fill with the current effective due date (yyyy-mm-dd).
    const initial = dueIso ? new Date(dueIso) : new Date();
    const y = initial.getFullYear();
    const m = String(initial.getMonth() + 1).padStart(2, "0");
    const d = String(initial.getDate()).padStart(2, "0");
    setEditingDueValue(`${y}-${m}-${d}`);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const bulkDelete = async () => {
    if (typedConfirm !== "DELETE") return;
    setDeleting(true);
    try {
      const { data } = await axios.post(`${API}/jobs/bulk-delete`, {
        ids: Array.from(selectedIds),
      });
      toast.success(
        `Archived ${data.deleted} job${data.deleted === 1 ? "" : "s"} and all their files`,
      );
      setConfirming(false);
      setTypedConfirm("");
      clearSelection();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setDeleting(false);
    }
  };

  const selectedCount = selectedIds.size;
  const anySelected = selectedCount > 0;

  // Status filter counts + filtered list
  const counts = useMemo(() => {
    const c = { all: jobs.length, in_progress: 0, ready_for_review: 0, closed: 0 };
    for (const j of jobs) {
      if (j.status in c) c[j.status] += 1;
    }
    return c;
  }, [jobs]);
  const visibleJobs = useMemo(
    () => (filter === "all" ? jobs : jobs.filter((j) => j.status === filter)),
    [jobs, filter],
  );
  const allVisibleSelected =
    visibleJobs.length > 0 &&
    visibleJobs.every((j) => selectedIds.has(j.id));
  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        visibleJobs.forEach((j) => next.delete(j.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        visibleJobs.forEach((j) => next.add(j.id));
        return next;
      });
    }
  };

  return (
    <div className="min-h-screen bg-background text-slate-900">
      <AppTopBar />

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10 pb-24">
        <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">
              Workspace
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter">
              {isAdmin ? "All jobs" : "My jobs"}
            </h1>
            <p className="text-slate-500 mt-2 font-medium max-w-xl">
              {isAdmin
                ? "Create, assign, and monitor every job. Multi-select to archive completed work in bulk."
                : "Upload originals, deliver edited results, then commit for admin review."}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {isAdmin && visibleJobs.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={toggleSelectAllVisible}
                className="border-border text-slate-700 hover:bg-slate-50 h-10 rounded-md"
                data-testid="btn-select-all"
              >
                {allVisibleSelected ? "Clear selection" : "Select all"}
              </Button>
            )}
            {isAdmin && (
              <Button
                onClick={() => nav("/group-portrait-tasks")}
                variant="outline"
                size="sm"
                className="border-border text-slate-700 hover:bg-slate-50 h-10 rounded-md font-semibold"
                data-testid="btn-group-portraits"
                title="Queue up Group→Portrait tasks that run in the background"
              >
                <UsersRound className="h-4 w-4 mr-1.5" />
                Group → Portraits
              </Button>
            )}
            {isAdmin && (
              <Button
                onClick={() => nav("/preschool-tool")}
                variant="outline"
                size="sm"
                className="border-border text-slate-700 hover:bg-slate-50 h-10 rounded-md font-semibold"
                data-testid="btn-preschool-tool"
                title="Name preschool photos from the handwritten numbers sheet"
              >
                <Baby className="h-4 w-4 mr-1.5" />
                Preschool
              </Button>
            )}
            {isAdmin && (
              <Button
                onClick={() => setCreating(true)}
                className="bg-primary hover:bg-primary/90 text-primary-foreground h-10 rounded-md font-semibold shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow]"
                data-testid="btn-new-job"
              >
                <Plus className="h-4 w-4 mr-1.5" />New job
              </Button>
            )}
          </div>
        </div>

        {/* Status filter tabs — Awaiting review is prominent because that
            is what admins care about most day-to-day. */}
        <div
          className="flex items-center gap-1 mb-6 border-b border-border overflow-x-auto"
          data-testid="job-filter-tabs"
        >
          {[
            { key: "all", label: "All" },
            { key: "in_progress", label: "In progress" },
            { key: "ready_for_review", label: "Awaiting review" },
            { key: "closed", label: "Closed" },
          ].map((t) => {
            const active = filter === t.key;
            const emphasize =
              t.key === "ready_for_review" && counts.ready_for_review > 0;
            return (
              <button
                key={t.key}
                onClick={() => setFilter(t.key)}
                className={`relative px-4 py-2.5 text-sm font-semibold transition-colors flex items-center gap-2
                  ${active
                    ? "text-primary"
                    : "text-slate-600 hover:text-slate-900"}`}
                data-testid={`job-filter-${t.key}`}
              >
                {t.label}
                <span
                  className={`inline-flex items-center rounded-full px-1.5 py-0 text-[10px] font-bold tabular-nums min-w-[18px] h-[18px] justify-center
                    ${emphasize
                      ? "bg-accent text-accent-foreground"
                      : active
                      ? "bg-primary/10 text-primary"
                      : "bg-slate-100 text-slate-500"}`}
                  data-testid={`job-filter-count-${t.key}`}
                >
                  {counts[t.key]}
                </span>
                {active && (
                  <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-primary rounded-full" />
                )}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-32 bg-slate-100 animate-pulse rounded-md" />
            ))}
          </div>
        ) : visibleJobs.length === 0 ? (
          <div className="border-2 border-dashed border-border rounded-md px-8 py-16">
            <ClipboardList className="h-8 w-8 text-slate-400 mb-4" />
            <p className="font-heading text-2xl font-bold tracking-tight mb-1">
              {filter === "all"
                ? isAdmin ? "No jobs yet." : "No jobs assigned to you yet."
                : filter === "ready_for_review"
                ? "Nothing waiting for review."
                : filter === "closed"
                ? "No closed jobs."
                : "No jobs in progress."}
            </p>
            <p className="text-slate-500 mb-6 font-medium">
              {filter === "all" && isAdmin
                ? "Kick off the workflow by creating your first job."
                : filter === "all"
                ? "Ask your admin to assign a job to your account."
                : "Try switching to a different status."}
            </p>
            {isAdmin && filter === "all" && (
              <Button
                onClick={() => setCreating(true)}
                className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-md h-10"
              >
                <Plus className="h-4 w-4 mr-1.5" />Create new job
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="jobs-grid">
            {visibleJobs.map((j) => {
              const selected = selectedIds.has(j.id);
              return (
                <div
                  key={j.id}
                  className={`group relative bg-white border rounded-md p-5 transition-all
                    hover:shadow-md hover:-translate-y-0.5 focus-within:shadow-md
                    ${selected ? "border-primary ring-2 ring-primary/30" : "border-border hover:border-primary/50"}
                    ${anySelected ? "" : ""}`}
                  data-testid={`job-card-${j.id}`}
                >
                  {/* Selection checkbox — hover-reveal, sticky if any selected */}
                  {isAdmin && (
                    <div
                      className={`absolute top-3 left-3 transition-opacity ${
                        anySelected || selected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                      }`}
                    >
                      <Checkbox
                        checked={selected}
                        onCheckedChange={() => toggleSelect(j.id)}
                        className="border-slate-300 data-[state=checked]:bg-primary data-[state=checked]:border-primary h-5 w-5"
                        data-testid={`job-select-${j.id}`}
                      />
                    </div>
                  )}

                  <Link
                    to={`/jobs/${j.id}`}
                    className={`block ${isAdmin ? "pl-8" : ""}`}
                    onClick={(e) => { if (anySelected && isAdmin) { e.preventDefault(); toggleSelect(j.id); } }}
                    data-testid={`job-open-${j.id}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Folder className="h-4 w-4 text-primary shrink-0" />
                        <h3 className="font-heading font-bold truncate">{j.name}</h3>
                      </div>
                      <StatusPill status={j.status} />
                    </div>
                    {j.description && (
                      <p className="text-sm text-slate-500 mb-4 line-clamp-2 font-medium">
                        {j.description}
                      </p>
                    )}
                    <div className="flex items-center justify-between text-xs text-slate-500 font-semibold tabular-nums mb-2">
                      {isAdmin && (
                        <span data-testid={`job-assignee-${j.id}`} className="truncate max-w-[60%]">
                          {j.assigned_user_email || "Unassigned"}
                        </span>
                      )}
                      <span className="text-slate-700">
                        {j.uploaded_count} <span className="text-slate-400">/</span> {j.edited_count}
                      </span>
                    </div>
                    {/* Dates: uploaded (created) + due (persisted or auto-computed) */}
                    {(() => {
                      const due = _dueMeta(j);
                      const isEditingDue = editingDueId === j.id;
                      return (
                        <div className="flex items-center justify-between text-[11px] font-medium border-t border-slate-100 pt-2"
                             data-testid={`job-dates-${j.id}`}>
                          <span className="inline-flex items-center gap-1 text-slate-500"
                                title={j.created_at ? new Date(j.created_at).toLocaleString() : ""}>
                            <CalendarDays className="h-3 w-3" />
                            <span className="text-slate-400 uppercase tracking-wider text-[9px]">Uploaded</span>
                            <span className="text-slate-700 ml-0.5" data-testid={`job-uploaded-date-${j.id}`}>
                              {_shortDate(j.created_at)}
                            </span>
                          </span>
                          {isEditingDue && isAdmin ? (
                            <span
                              className="inline-flex items-center gap-1"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                              data-testid={`job-due-edit-${j.id}`}
                            >
                              <input
                                type="date"
                                value={editingDueValue}
                                onChange={(e) => setEditingDueValue(e.target.value)}
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") { e.preventDefault(); saveDueDate(j.id); }
                                  if (e.key === "Escape") { e.preventDefault(); setEditingDueId(null); }
                                }}
                                disabled={savingDue}
                                autoFocus
                                className="h-6 text-[11px] font-medium px-1.5 rounded-sm border border-slate-300 focus:border-primary focus:outline-none"
                                data-testid={`job-due-input-${j.id}`}
                              />
                              <button
                                type="button"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); saveDueDate(j.id); }}
                                disabled={savingDue}
                                className="h-6 w-6 inline-flex items-center justify-center rounded-sm bg-primary text-white hover:bg-primary/90 disabled:opacity-60"
                                title="Save due date"
                                data-testid={`job-due-save-${j.id}`}
                              >
                                <Check className="h-3 w-3" />
                              </button>
                              <button
                                type="button"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditingDueId(null); }}
                                disabled={savingDue}
                                className="h-6 w-6 inline-flex items-center justify-center rounded-sm bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-60"
                                title="Cancel"
                                data-testid={`job-due-cancel-${j.id}`}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          ) : (
                            <span className={`group/due inline-flex items-center gap-1 ${
                                    due.overdue ? "text-red-600" : "text-slate-500"
                                  }`}
                                  title={due.dueIso ? `Due ${new Date(due.dueIso).toLocaleString()}${due.source === "manual" ? "" : " · auto (created + 3 days)"}` : ""}>
                              <Clock className="h-3 w-3" />
                              <span className={`uppercase tracking-wider text-[9px] ${
                                      due.overdue ? "text-red-600" : "text-slate-400"
                                    }`}>Due</span>
                              <span className={`ml-0.5 ${due.overdue ? "font-bold" : "text-slate-700"}`}
                                    data-testid={`job-due-date-${j.id}`}>
                                {due.date}{due.overdue ? " · Overdue" : ""}
                              </span>
                              {isAdmin && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    startEditingDue(j, due.dueIso);
                                  }}
                                  className="opacity-0 group-hover/due:opacity-100 focus:opacity-100 h-4 w-4 -mr-1 inline-flex items-center justify-center rounded-sm text-slate-400 hover:text-primary hover:bg-primary/10 transition-opacity"
                                  title="Edit due date"
                                  data-testid={`job-due-edit-btn-${j.id}`}
                                >
                                  <Pencil className="h-2.5 w-2.5" />
                                </button>
                              )}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Floating bulk-action bar (Crystal Glassmorphism) */}
      {isAdmin && anySelected && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-white/85 backdrop-blur-xl border border-slate-200 shadow-2xl rounded-full px-4 py-2 flex items-center gap-3"
          data-testid="bulk-action-bar"
          style={{ animation: "float-up 200ms ease-out" }}
        >
          <span className="text-sm font-semibold text-slate-700 pl-2">
            {selectedCount} selected
          </span>
          <div className="h-4 w-px bg-slate-300" />
          <Button
            variant="ghost"
            size="sm"
            onClick={clearSelection}
            className="rounded-full h-8 text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            data-testid="btn-clear-selection"
          >
            <X className="h-3.5 w-3.5 mr-1.5" />Clear
          </Button>
          <Button
            size="sm"
            onClick={() => { setConfirming(true); setTypedConfirm(""); }}
            className="rounded-full h-8 bg-destructive hover:bg-red-700 text-destructive-foreground font-semibold"
            data-testid="btn-bulk-archive"
          >
            <Archive className="h-3.5 w-3.5 mr-1.5" />Archive &amp; delete
          </Button>
        </div>
      )}
      <style>{`
        @keyframes float-up {
          from { opacity: 0; transform: translate(-50%, 12px); }
          to   { opacity: 1; transform: translate(-50%, 0); }
        }
      `}</style>

      {/* Create job dialog */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="bg-white border border-border rounded-md max-w-md text-slate-900">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold tracking-tight text-slate-900">
              Create job
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              A new job with empty <code className="text-slate-700 bg-slate-100 px-1 rounded-sm">uploaded/</code> and <code className="text-slate-700 bg-slate-100 px-1 rounded-sm">edited/</code> folders will be created and assigned.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Job name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Smith wedding — reception"
                className="bg-white text-slate-900 placeholder:text-slate-400 border border-slate-300 rounded-md h-10 focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary"
                data-testid="new-job-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Description (optional)</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Any notes for the editor — deadline, style, etc."
                className="bg-white text-slate-900 placeholder:text-slate-400 border border-slate-300 rounded-md min-h-[80px] focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary"
                data-testid="new-job-desc"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Assign to</Label>
              <Select
                value={form.assigned_user_id}
                onValueChange={(v) => setForm({ ...form, assigned_user_id: v })}
              >
                <SelectTrigger className="bg-white text-slate-900 border border-slate-300 rounded-md h-10 focus:ring-2 focus:ring-primary" data-testid="new-job-assignee">
                  <SelectValue placeholder="Pick a user…" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.email} {u.role === "admin" ? "· admin" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreating(false)}
              className="text-slate-700 hover:bg-slate-100 rounded-md">
              Cancel
            </Button>
            <Button
              onClick={createJob}
              disabled={!form.name.trim() || !form.assigned_user_id}
              className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-md"
              data-testid="new-job-submit"
            >
              Create job
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk-archive confirmation modal — Swiss brutalist */}
      <Dialog open={confirming} onOpenChange={(o) => { if (!o) { setConfirming(false); setTypedConfirm(""); } }}>
        <DialogContent
          className="bg-white border-2 border-destructive rounded-md max-w-md"
          data-testid="bulk-archive-dialog"
        >
          <DialogHeader>
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-6 w-6 text-destructive shrink-0 mt-0.5" />
              <div>
                <DialogTitle className="font-heading text-2xl font-black tracking-tight text-slate-900">
                  Delete {selectedCount} job{selectedCount === 1 ? "" : "s"}?
                </DialogTitle>
                <DialogDescription className="text-slate-600 mt-1">
                  This will permanently delete the selected jobs <span className="font-bold text-slate-900">and every file in their uploaded and edited folders</span>. This cannot be undone.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">
              Type <span className="text-destructive font-black">DELETE</span> to confirm
            </Label>
            <Input
              value={typedConfirm}
              onChange={(e) => setTypedConfirm(e.target.value)}
              placeholder="DELETE"
              className="bg-white text-slate-900 placeholder:text-slate-400 border border-slate-300 rounded-md h-10 font-mono focus-visible:ring-2 focus-visible:ring-destructive focus-visible:border-destructive"
              data-testid="bulk-archive-confirm-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => { setConfirming(false); setTypedConfirm(""); }}
              className="text-slate-600 hover:bg-slate-100 rounded-md"
            >
              Cancel
            </Button>
            <Button
              onClick={bulkDelete}
              disabled={typedConfirm !== "DELETE" || deleting}
              className="bg-destructive hover:bg-red-700 text-destructive-foreground rounded-md font-semibold"
              data-testid="bulk-archive-submit"
            >
              <Trash2 className="h-4 w-4 mr-1.5" />
              {deleting ? "Deleting…" : `Delete ${selectedCount} job${selectedCount === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <GroupPortraitsDialog
        open={portraitsOpen}
        onClose={() => setPortraitsOpen(false)}
      />
    </div>
  );
}
